import { PAYMIX_DEFAULT_VARIATION_PCT, PAYMIX_MAX_VARIATION_PCT } from "@shared/constants/paymix";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  FlaskConical,
  Info,
  Loader2,
  Zap,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { findMatchingBand } from "@/components/offer-recommendation/find-matching-band";
import {
  MarketCompCard,
  type MarketCompEstimate,
} from "@/components/offer-recommendation/market-comp-card";
import { RecommendationLoader } from "@/components/offer-recommendation/recommendation-loader";
import { PaymixCalculator } from "@/components/paymix-calculator";
import { AutosizeTextarea } from "@/components/ui/autosize-textarea";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ExpandableText } from "@/components/ui/expandable-text";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import { JOB_LEVELS } from "@/lib/constants";
import { queryClient } from "@/lib/queryClient";
import { orpc, rpcClient } from "@/lib/rpc";
import { OfferExcitementBlock } from "./candidate-excitement-display";

type Recommendation = {
  id: string;
  salary: string;
  equity: string;
  zone: string;
  bandPosition: string;
  reasoning: {
    differentiators: Array<{
      signal: string;
      source: string[];
      convergent: boolean;
      points: number;
    }>;
    totalPoints: number;
    contextNotes: string[];
    equityCheck: { peerComparison: string };
    justification: string;
  };
};

type ExcitementSignal = {
  signal: string;
  source: string[];
  convergent: boolean;
  points: number;
};

type ExcitementResult = {
  id: string;
  excitementScore: number;
  rationale: string;
  signals: ExcitementSignal[];
  marketCompEstimate: MarketCompEstimate | null;
};

type FailureStage = "excitement" | "recommendation" | "persistence";
type GenerationFailure = {
  status: "failed";
  stage: FailureStage;
  errorMessage: string;
  generationId: string;
};

type ExcitementResponse =
  | ({ status: "succeeded" } & ExcitementResult)
  | {
      status: "failed";
      stage: "excitement" | "persistence";
      errorMessage: string;
      generationId: string;
    };

type RecommendationResponse =
  | {
      status: "succeeded";
      id: string;
      salary: string;
      equity: string;
      zone: string;
      bandPosition: string;
      reasoning: Recommendation["reasoning"];
    }
  | {
      status: "failed";
      stage: "recommendation" | "persistence";
      errorMessage: string;
      generationId: string;
    };

const STAGE_MESSAGES: Record<FailureStage, string> = {
  excitement:
    "A análise de excitement do candidato falhou. Você pode tentar novamente ou preencher a oferta manualmente.",
  recommendation:
    "A geração da recomendação de oferta falhou. Você pode tentar novamente ou preencher manualmente.",
  persistence:
    "A recomendação foi calculada, mas não conseguimos salvar. Tente novamente em instantes ou preencha manualmente.",
};

const ZONE_LABELS: Record<string, string> = {
  baseline: "Baseline",
  solid: "Sólido",
  strong: "Forte",
  exceptional: "Excepcional",
  outlier: "Outlier",
};

function zoneLabel(zone: string): string {
  return ZONE_LABELS[zone.toLowerCase()] ?? zone;
}

function scoreLabel(score: number): string {
  if (score >= 9) return "Excepcional";
  if (score >= 7) return "Forte";
  if (score >= 5) return "Médio";
  if (score >= 3) return "Fraco";
  return "Muito fraco";
}

function scoreColor(score: number): string {
  if (score >= 8)
    return "bg-[hsl(var(--forest-mist))] text-[hsl(var(--forest-pulse))] border-[hsl(var(--forest-pulse))]/30";
  if (score >= 5)
    return "bg-[hsl(var(--amber-mist))] text-[hsl(var(--amber-state))] border-[hsl(var(--amber-state))]/30";
  return "bg-[hsl(var(--coral-mist))] text-[hsl(var(--alert-red))] border-[hsl(var(--alert-red))]/30";
}

function scoreTextColor(score: number): string {
  if (score >= 8) return "text-[hsl(var(--forest-pulse))]";
  if (score >= 5) return "text-[hsl(var(--amber-state))]";
  return "text-[hsl(var(--alert-red))]";
}

function buildDeviationFields(
  rec: Recommendation,
  adjusted: { salary: string; equity: string; signingBonus: string; performanceBonus: string },
) {
  const out: Record<string, { recommended: string; adjusted: string }> = {};
  if (adjusted.salary && adjusted.salary !== rec.salary) {
    out.salary = { recommended: rec.salary, adjusted: adjusted.salary };
  }
  if (adjusted.equity && adjusted.equity !== rec.equity) {
    out.equity = { recommended: rec.equity, adjusted: adjusted.equity };
  }
  if (adjusted.signingBonus) {
    out.signingBonus = { recommended: "0", adjusted: adjusted.signingBonus };
  }
  if (adjusted.performanceBonus) {
    out.performanceBonus = { recommended: "0", adjusted: adjusted.performanceBonus };
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

type Step = 1 | "loading-exc" | 2 | "loading-rec" | 3;

type CreateOfferDialogProps = {
  applicationId: string;
  applicantName: string;
  jobTitle: string;
  defaultJobLevel?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onOfferCreated?: () => void;
  mode?: "create" | "simulate";
};

export function CreateOfferDialog({
  applicationId,
  applicantName,
  jobTitle,
  defaultJobLevel,
  open,
  onOpenChange,
  onOfferCreated,
  mode = "create",
}: CreateOfferDialogProps) {
  const isSimulate = mode === "simulate";
  const { toast } = useToast();
  const { isSuperAdmin } = useAuth();

  const [step, setStep] = useState<Step>(isSimulate ? 3 : 1);
  const [excitement, setExcitement] = useState<ExcitementResult | null>(null);
  const [recommendation, setRecommendation] = useState<Recommendation | null>(null);
  const [generationFailure, setGenerationFailure] = useState<GenerationFailure | null>(null);
  const [generationError, setGenerationError] = useState<string | null>(null);
  const [signalsExpanded, setSignalsExpanded] = useState(false);
  const [enableOverride, setEnableOverride] = useState(false);
  const [overriddenScore, setOverriddenScore] = useState<number | null>(null);
  const [overrideReason, setOverrideReason] = useState("");
  const [allowEdit, setAllowEdit] = useState(false);
  const [deviationReason, setDeviationReason] = useState("");
  const [autofilled, setAutofilled] = useState(false);
  const [jobLevel, setJobLevel] = useState(defaultJobLevel || "");
  const [salaryBase, setSalaryBase] = useState("");
  const [equityShares, setEquityShares] = useState("");
  const [signingBonus, setSigningBonus] = useState("");
  const [performanceBonus, setPerformanceBonus] = useState("");
  const [performanceBonusShares, setPerformanceBonusShares] = useState("");
  const [performanceBonusSharesMax, setPerformanceBonusSharesMax] = useState("");
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [templateContent, setTemplateContent] = useState("");
  const [selectedSalaryTableId, setSelectedSalaryTableId] = useState("");
  const [fxRate, setFxRate] = useState("5.80");
  const [allowPaymixAdjustment, setAllowPaymixAdjustment] = useState(false);
  const [paymixVariationPct, setPaymixVariationPct] = useState(PAYMIX_DEFAULT_VARIATION_PCT);

  const [paymixSalary, setPaymixSalary] = useState(0);
  const [paymixShares, setPaymixShares] = useState(0);
  const [paymixInitialized, setPaymixInitialized] = useState(false);
  const [marketEstimate, setMarketEstimate] = useState<MarketCompEstimate | null>(null);

  const resetForm = useCallback(() => {
    setStep(isSimulate ? 3 : 1);
    setJobLevel(defaultJobLevel || "");
    setSalaryBase("");
    setEquityShares("");
    setSigningBonus("");
    setPerformanceBonus("");
    setPerformanceBonusShares("");
    setPerformanceBonusSharesMax("");
    setSelectedTemplateId("");
    setTemplateContent("");
    setSelectedSalaryTableId("");
    setFxRate("5.80");
    setAllowPaymixAdjustment(false);
    setPaymixVariationPct(PAYMIX_DEFAULT_VARIATION_PCT);
    setPaymixSalary(0);
    setPaymixShares(0);
    setPaymixInitialized(false);
    setExcitement(null);
    setRecommendation(null);
    setGenerationFailure(null);
    setGenerationError(null);
    setSignalsExpanded(false);
    setEnableOverride(false);
    setOverriddenScore(null);
    setOverrideReason("");
    setAllowEdit(false);
    setDeviationReason("");
    setAutofilled(false);
    setMarketEstimate(null);
  }, [defaultJobLevel, isSimulate]);

  useEffect(() => {
    setJobLevel(defaultJobLevel || "");
  }, [defaultJobLevel]);

  // Queries
  const { data: salaryTables } = useQuery({
    ...orpc.salaryTables.listTables.queryOptions(),
    enabled: open,
  });

  const { data: salaryBands = [] } = useQuery({
    ...orpc.salaryTables.listBands.queryOptions({ input: { id: selectedSalaryTableId } }),
    enabled: !!selectedSalaryTableId,
  });

  const { data: templates } = useQuery({
    ...orpc.offerTemplates.list.queryOptions(),
    enabled: open,
  });

  const { data: investmentRoundsData } = useQuery({
    ...orpc.salaryTables.investmentRounds.queryOptions(),
    enabled: open,
  });

  // Candidate-side excitement scores (PR2: read-only). Shown side-by-side with
  // the manager excitement to surface the closing gap that PR3 will use to
  // modulate the recommendation formula.
  const { data: candidateExcitementSeries = [] } = useQuery({
    ...orpc.candidateExcitement.listByApplication.queryOptions({
      input: { applicationId },
    }),
    enabled: open && !!applicationId,
  });

  useEffect(() => {
    if (templates && templates.length > 0 && !selectedTemplateId) {
      const defaultTemplate = templates.find((t) => t.isDefault) || templates[0];
      setSelectedTemplateId(defaultTemplate.id);
      setTemplateContent(defaultTemplate.htmlContent);
    }
  }, [templates, selectedTemplateId]);

  const matchingBand = findMatchingBand(salaryBands, jobLevel);

  useEffect(() => {
    if (matchingBand) {
      const salaryBRL = (matchingBand.midValue * 0.9) / 100;
      setSalaryBase(salaryBRL.toFixed(2));
      setEquityShares(matchingBand.sopMeio > 0 ? String(matchingBand.sopMeio) : "");
    }
  }, [matchingBand]);

  const sharesValue = parseInt(equityShares, 10) || 0;
  const canEnablePaymix = sharesValue > 0;

  useEffect(() => {
    if (!canEnablePaymix) {
      setAllowPaymixAdjustment(false);
    }
  }, [canEnablePaymix]);

  const bandPosition = (() => {
    if (!matchingBand || !salaryBase) return null;
    const salary = Number.parseFloat(salaryBase);
    if (!Number.isFinite(salary)) return null;
    const min = matchingBand.minValue / 100;
    const max = matchingBand.maxValue / 100;
    const mid = (min + max) / 2;
    if (mid === 0) return null;
    return (salary / mid) * 100;
  })();

  const handleTemplateSelect = (templateId: string) => {
    setSelectedTemplateId(templateId);
    const selected = templates?.find((t) => t.id === templateId);
    if (selected) {
      setTemplateContent(selected.htmlContent);
    }
  };

  const createMutation = useMutation({
    mutationFn: async () => {
      const finalSalary = allowPaymixAdjustment && paymixInitialized ? paymixSalary : salaryBase;
      const finalShares =
        allowPaymixAdjustment && paymixInitialized ? paymixShares : parseInt(equityShares, 10);

      const followed = recommendation ? !allowEdit : undefined;
      const deviationFields =
        recommendation && allowEdit
          ? buildDeviationFields(recommendation, {
              salary: String(finalSalary),
              equity: String(finalShares ?? ""),
              signingBonus,
              performanceBonus,
            })
          : undefined;

      return rpcClient.offers.create({
        applicationId,
        jobLevel: jobLevel || undefined,
        salaryBase: String(finalSalary),
        equityShares: finalShares || undefined,
        signingBonus: signingBonus || undefined,
        performanceBonus: performanceBonus || undefined,
        performanceBonusShares: parseInt(performanceBonusShares, 10) || undefined,
        performanceBonusSharesMax: parseInt(performanceBonusSharesMax, 10) || undefined,
        offerLetterTemplate: templateContent,
        allowPaymixAdjustment,
        paymixVariationPct: allowPaymixAdjustment ? paymixVariationPct : undefined,
        fxRate: fxRate || undefined,
        salaryTableId: selectedSalaryTableId || undefined,
        salaryTableBandId: matchingBand?.id || undefined,
        offerRecommendationId: recommendation?.id,
        excitementScoreId: excitement?.id,
        hmFollowedRecommendation: followed,
        hmDeviationReason: allowEdit ? deviationReason : undefined,
        hmDeviationFields: deviationFields,
        marketCompEstimate: marketEstimate,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: orpc.offers.list.queryOptions().queryKey });
      queryClient.invalidateQueries({ queryKey: orpc.applications.list.queryOptions().queryKey });
      queryClient.invalidateQueries({
        queryKey: orpc.applications.getDetails.queryOptions({ input: { id: applicationId } })
          .queryKey,
      });
      toast({
        title: "Oferta criada",
        description: "A oferta foi enviada para aprovação dos superadmins via Slack.",
      });
      onOpenChange(false);
      onOfferCreated?.();
      resetForm();
    },
    onError: (error: Error) => {
      toast({
        title: "Erro",
        description: error.message || "Não foi possível criar a oferta.",
        variant: "destructive",
      });
    },
  });

  const generateExcitementStep = async (isRetry = false) => {
    setGenerationError(null);
    setGenerationFailure(null);
    setStep("loading-exc");
    if (isRetry) {
      toast({ title: "Tentando novamente", description: "Avaliando excitement…" });
    }
    try {
      const result = (await rpcClient.offerRecommendationGenerations.generateExcitement({
        applicationId,
        salaryTableId: selectedSalaryTableId || undefined,
        salaryTableBandId: matchingBand?.id || undefined,
      })) as ExcitementResponse;
      if (result.status === "failed") {
        setGenerationFailure(result);
        return;
      }
      setExcitement({
        id: result.id,
        excitementScore: result.excitementScore,
        rationale: result.rationale,
        signals: result.signals ?? [],
        marketCompEstimate: result.marketCompEstimate ?? null,
      });
      setMarketEstimate(result.marketCompEstimate ?? null);
      setOverriddenScore(result.excitementScore);
      setStep(2);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Erro inesperado.";
      setGenerationError(message);
    }
  };

  const generateRecommendationStep = async (isRetry = false) => {
    if (!excitement) return;

    if (enableOverride && !overrideReason.trim()) {
      toast({
        title: "Motivo obrigatório",
        description: "Explique o motivo do ajuste do excitement score.",
        variant: "destructive",
      });
      return;
    }

    setGenerationError(null);
    setGenerationFailure(null);
    setStep("loading-rec");
    if (isRetry) {
      // biome-ignore lint/security/noSecrets: UI toast copy, not a secret
      toast({ title: "Tentando novamente", description: "Gerando a recomendação…" });
    }
    try {
      let scoreForRec = excitement.excitementScore;
      if (
        enableOverride &&
        overriddenScore !== null &&
        overriddenScore !== excitement.excitementScore
      ) {
        await rpcClient.offerRecommendationGenerations.overrideExcitement({
          excitementScoreId: excitement.id,
          overriddenScore,
          overrideReason: overrideReason.trim(),
        });
        scoreForRec = overriddenScore;
      }

      const result = (await rpcClient.offerRecommendationGenerations.generateRecommendation({
        applicationId,
        excitementScoreId: excitement.id,
        overriddenScore:
          enableOverride &&
          overriddenScore !== null &&
          overriddenScore !== excitement.excitementScore
            ? overriddenScore
            : undefined,
        salaryTableId: selectedSalaryTableId || undefined,
        salaryTableBandId: matchingBand?.id || undefined,
        fxRate: fxRate || undefined,
      })) as RecommendationResponse;

      if (result.status === "failed") {
        setGenerationFailure(result);
        return;
      }
      void scoreForRec;
      setRecommendation({
        id: result.id,
        salary: result.salary,
        equity: result.equity,
        zone: result.zone,
        bandPosition: result.bandPosition,
        reasoning: result.reasoning,
      });
      setAutofilled(false);
      setStep(3);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Erro inesperado.";
      setGenerationError(message);
    }
  };

  const handleManualFallback = () => {
    if (generationFailure) {
      setDeviationReason(
        `Geração automática falhou (${generationFailure.stage}) — ref ${generationFailure.generationId}. Preenchimento manual.`,
      );
    }
    setExcitement(null);
    setRecommendation(null);
    setGenerationFailure(null);
    setGenerationError(null);
    setAutofilled(false);
    setSalaryBase("");
    setEquityShares("");
    setSigningBonus("");
    setPerformanceBonus("");
    setAllowEdit(true);
    setStep(3);
  };

  const handleNext = () => {
    if (!selectedSalaryTableId) {
      toast({
        title: "Campos obrigatórios",
        description: "Selecione uma tabela salarial.",
        variant: "destructive",
      });
      return;
    }
    if (!selectedTemplateId) {
      toast({
        title: "Campos obrigatórios",
        description: "Selecione um template de carta de oferta.",
        variant: "destructive",
      });
      return;
    }
    void generateExcitementStep();
  };

  // Autofill Step 3 from the recommendation, once
  useEffect(() => {
    if (step === 3 && recommendation && !autofilled) {
      setSalaryBase(recommendation.salary);
      setEquityShares(recommendation.equity);
      setSigningBonus("");
      setPerformanceBonus("");
      setAutofilled(true);
    }
  }, [step, recommendation, autofilled]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isSimulate) return;
    if (!salaryBase) {
      toast({
        title: "Campos obrigatórios",
        description: "Preencha o salário base.",
        variant: "destructive",
      });
      return;
    }
    if (recommendation && allowEdit && !deviationReason.trim()) {
      toast({
        title: "Motivo obrigatório",
        description: "Explique o motivo da alteração para prosseguir.",
        variant: "destructive",
      });
      return;
    }
    createMutation.mutate();
  };

  const handleOpenChange = (newOpen: boolean) => {
    onOpenChange(newOpen);
    if (!newOpen) {
      resetForm();
    }
  };

  const handlePaymixValuesChange = useCallback((values: { salary: number; shares: number }) => {
    setPaymixSalary(values.salary);
    setPaymixShares(values.shares);
    setPaymixInitialized(true);
  }, []);

  const salaryTableOptions = salaryTables?.map((t) => ({ value: t.id, label: t.name })) ?? [];
  const jobLevelOptions = JOB_LEVELS.map((l) => ({ value: l.value, label: l.label }));
  const templateOptions =
    templates?.map((t) => ({
      value: t.id,
      label: `${t.name}${t.isDefault ? " (Padrão)" : ""}`,
    })) ?? [];

  const rounds = investmentRoundsData?.rounds ?? [];
  const strikePrice = investmentRoundsData?.strikePrice ?? 0;
  const fxRateNum = parseFloat(fxRate) || 5;
  const salaryBaseNum = parseFloat(salaryBase) || 0;

  // Derived totals used by MarketCompCard (mirrors PaymixCalculator math).
  const marketCardCurrentRound = rounds.find((r) => r.section === "current") ?? rounds[0];
  const marketCardSpread = marketCardCurrentRound
    ? marketCardCurrentRound.pricePerShare - strikePrice
    : 0;
  const marketCardEquityAnnualBRL =
    sharesValue > 0 && marketCardSpread > 0
      ? ((sharesValue * marketCardSpread) / 4) * fxRateNum
      : 0;
  const marketCardPerfBonusSharesNum =
    parseInt(performanceBonusSharesMax, 10) || parseInt(performanceBonusShares, 10) || 0;
  const marketCardPerfBonusSharesAnnualBRL =
    marketCardPerfBonusSharesNum > 0 && marketCardSpread > 0
      ? ((marketCardPerfBonusSharesNum * marketCardSpread) / 4) * fxRateNum
      : 0;

  const stepDot = (n: 1 | 2 | 3, label: string) => {
    const stepNum: number =
      step === "loading-exc" ? 1 : step === "loading-rec" ? 2 : (step as number);
    const completed = stepNum > n;
    const active = stepNum === n;
    return (
      <div className="flex items-center gap-2">
        <div
          className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-semibold border ${
            completed
              ? "bg-[hsl(var(--forest-pulse))] text-white border-[hsl(var(--forest-pulse))]"
              : active
                ? "bg-primary text-primary-foreground border-primary"
                : "bg-muted text-muted-foreground border-border"
          }`}
        >
          {completed ? <Check className="h-3.5 w-3.5" /> : n}
        </div>
        <span
          className={`text-xs font-medium ${active ? "text-foreground" : "text-muted-foreground"}`}
        >
          {label}
        </span>
      </div>
    );
  };

  const usedScore = recommendation
    ? enableOverride &&
      overriddenScore !== null &&
      excitement &&
      overriddenScore !== excitement.excitementScore
      ? overriddenScore
      : (excitement?.excitementScore ?? 0)
    : (excitement?.excitementScore ?? 0);

  const wasOverridden =
    !!excitement &&
    enableOverride &&
    overriddenScore !== null &&
    overriddenScore !== excitement.excitementScore;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isSimulate && <FlaskConical className="h-4 w-4 text-muted-foreground" />}
            {isSimulate ? "Simular Oferta" : "Criar Oferta de Emprego"}
          </DialogTitle>
          <DialogDescription>
            Candidato: <strong>{applicantName}</strong> • Vaga: <strong>{jobTitle}</strong>
            {isSimulate && (
              <span className="block text-xs mt-0.5 text-muted-foreground">
                Modo simulação — nenhuma oferta será criada.
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        {!isSimulate && (
          <div className="flex items-center gap-3 my-2">
            {stepDot(1, "Config")}
            <div className="flex-1 h-px bg-border" />
            {stepDot(2, "Excitement")}
            <div className="flex-1 h-px bg-border" />
            {stepDot(3, "Oferta")}
          </div>
        )}

        {step === 1 && !isSimulate && (
          <div className="grid gap-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="salaryTable">
                  Tabela Salarial <span className="text-destructive">*</span>
                </Label>
                <SearchableSelect
                  id="salaryTable"
                  value={selectedSalaryTableId}
                  onValueChange={setSelectedSalaryTableId}
                  options={salaryTableOptions}
                  placeholder="Selecione uma tabela salarial"
                  searchPlaceholder="Buscar tabela..."
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="jobLevel">Nível</Label>
                <SearchableSelect
                  id="jobLevel"
                  value={jobLevel}
                  onValueChange={setJobLevel}
                  options={jobLevelOptions}
                  placeholder="Selecione o nível"
                  searchPlaceholder="Buscar nível..."
                  preserveOrder
                />
                {defaultJobLevel && jobLevel !== defaultJobLevel && (
                  <p className="text-xs text-muted-foreground">
                    Nível da vaga:{" "}
                    {JOB_LEVELS.find((l) => l.value === defaultJobLevel)?.label || defaultJobLevel}
                  </p>
                )}
              </div>
            </div>

            {selectedSalaryTableId && jobLevel && !matchingBand && (
              <div className="rounded-md border border-[hsl(var(--amber-state))]/30 bg-[hsl(var(--amber-mist))] px-3 py-2 text-sm text-[hsl(var(--ink-primary))]">
                Nenhuma faixa encontrada para o nível <strong>{jobLevel}</strong> nesta tabela. A
                recomendação de salário/equity vai retornar zerada — cadastre uma banda com chave{" "}
                <code>{jobLevel}</code> ou <code>{jobLevel.split("_")[0]}</code> antes de
                prosseguir.
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="fxRate">Taxa de Câmbio (R$/USD)</Label>
              <Input
                id="fxRate"
                type="number"
                step="0.01"
                value={fxRate}
                onChange={(e) => setFxRate(e.target.value)}
                placeholder="5.00"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="offerTemplate">
                Template de Carta de Oferta <span className="text-destructive">*</span>
              </Label>
              <SearchableSelect
                id="offerTemplate"
                value={selectedTemplateId}
                onValueChange={handleTemplateSelect}
                options={templateOptions}
                placeholder="Selecione um template"
                searchPlaceholder="Buscar template..."
              />
              {!templates?.length && (
                <p className="text-xs text-muted-foreground">
                  Nenhum template disponível. Crie um na página de Templates.
                </p>
              )}
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
                Cancelar
              </Button>
              <Button type="button" onClick={handleNext}>
                Próximo
                <ChevronRight className="ml-1 h-4 w-4" />
              </Button>
            </DialogFooter>
          </div>
        )}

        {step === "loading-exc" && (
          <RecommendationLoader
            status={generationFailure || generationError ? "error" : "running"}
            runningTitle="Avaliando excitement do candidato…"
            runningSubtitle="Estamos analisando o dossiê e identificando sinais relevantes."
            message={
              generationFailure
                ? STAGE_MESSAGES[generationFailure.stage]
                : (generationError ?? undefined)
            }
            detail={
              isSuperAdmin && generationFailure
                ? `${generationFailure.stage}: ${generationFailure.errorMessage} (ref ${generationFailure.generationId})`
                : undefined
            }
            onRetry={
              generationFailure || generationError
                ? () => void generateExcitementStep(true)
                : undefined
            }
            onManual={
              generationFailure
                ? handleManualFallback
                : generationError
                  ? () => {
                      setExcitement(null);
                      setRecommendation(null);
                      setGenerationError(null);
                      setStep(3);
                    }
                  : undefined
            }
          />
        )}

        {step === 2 && excitement && (
          <div className="grid gap-4">
            <div className="rounded-lg border bg-card px-4 py-3 shadow-sm">
              <div className="flex items-center gap-2">
                <span
                  className={`text-2xl font-bold ${scoreTextColor(excitement.excitementScore)}`}
                >
                  {excitement.excitementScore.toFixed(1)}
                </span>
                <Badge variant="outline" className={scoreColor(excitement.excitementScore)}>
                  {scoreLabel(excitement.excitementScore)}
                </Badge>
              </div>
              <ExpandableText collapsedLines={5} className="text-sm mt-1 text-muted-foreground">
                {excitement.rationale}
              </ExpandableText>
            </div>

            {excitement.signals.length > 0 && (
              <div className="rounded-lg border bg-slate-50 dark:bg-slate-900/40">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="w-full justify-between px-4 py-3"
                  onClick={() => setSignalsExpanded(!signalsExpanded)}
                >
                  <span className="text-sm font-medium">
                    Ver sinais identificados ({excitement.signals.length})
                  </span>
                  {signalsExpanded ? (
                    <ChevronUp className="h-4 w-4" />
                  ) : (
                    <ChevronDown className="h-4 w-4" />
                  )}
                </Button>
                {signalsExpanded && (
                  <div className="px-4 pb-4 space-y-3">
                    {excitement.signals.map((s) => (
                      <div
                        key={s.signal}
                        className={`flex items-start gap-3 rounded-md border px-3 py-2 ${
                          s.convergent
                            ? "bg-[hsl(var(--forest-mist))] border-[hsl(var(--forest-pulse))]/30"
                            : "bg-background"
                        }`}
                      >
                        {s.convergent ? (
                          <Check className="h-4 w-4 mt-0.5 text-[hsl(var(--forest-pulse))] shrink-0" />
                        ) : (
                          <Info className="h-4 w-4 mt-0.5 text-[hsl(var(--ink-muted))] shrink-0" />
                        )}
                        <div className="flex-1 min-w-0 space-y-1">
                          <div className="text-sm">{s.signal}</div>
                          {s.source.length > 0 && (
                            <div className="text-xs text-[hsl(var(--ink-muted))]">
                              Fontes: {s.source.join(", ")}
                            </div>
                          )}
                        </div>
                        {s.convergent && (
                          <span className="text-[11px] font-semibold text-[hsl(var(--forest-pulse))]">
                            Convergente
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="rounded-lg border bg-muted/30 px-4 py-3 space-y-3">
              <div className="flex items-center gap-3">
                <Switch
                  id="enableOverride"
                  checked={enableOverride}
                  onCheckedChange={(c) => {
                    const on = Boolean(c);
                    setEnableOverride(on);
                    if (on && overriddenScore === null) {
                      setOverriddenScore(excitement.excitementScore);
                    }
                  }}
                />
                <Label htmlFor="enableOverride" className="font-medium cursor-pointer">
                  Ajustar excitement score
                </Label>
              </div>
              {enableOverride && (
                <div className="space-y-3">
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="text-sm">Score ajustado</Label>
                      <span className="text-sm font-semibold">
                        {(overriddenScore ?? excitement.excitementScore).toFixed(1)} /{" "}
                        {scoreLabel(overriddenScore ?? excitement.excitementScore)}
                      </span>
                    </div>
                    <Slider
                      min={0}
                      max={10}
                      step={1}
                      value={[overriddenScore ?? excitement.excitementScore]}
                      onValueChange={(v) => setOverriddenScore(v[0] ?? 0)}
                    />
                    <div className="flex justify-between text-[10px] text-muted-foreground">
                      <span>0</span>
                      <span>5</span>
                      <span>10</span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="overrideReason" className="text-sm">
                      Motivo do ajuste <span className="text-destructive">*</span>
                    </Label>
                    <AutosizeTextarea
                      id="overrideReason"
                      value={overrideReason}
                      onChange={(e) => setOverrideReason(e.target.value)}
                      placeholder="Explique por que está ajustando o score…"
                      minHeight={72}
                    />
                  </div>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setStep(1)}>
                <ChevronLeft className="mr-1 h-4 w-4" />
                Voltar
              </Button>
              <Button type="button" onClick={() => void generateRecommendationStep()}>
                Gerar Recomendação
                <ChevronRight className="ml-1 h-4 w-4" />
              </Button>
            </DialogFooter>
          </div>
        )}

        {step === "loading-rec" && (
          <RecommendationLoader
            status={generationFailure || generationError ? "error" : "running"}
            runningTitle="Gerando recomendação de oferta…"
            runningSubtitle="Calculando salário e equity com base no excitement e na banda."
            message={
              generationFailure
                ? STAGE_MESSAGES[generationFailure.stage]
                : (generationError ?? undefined)
            }
            detail={
              isSuperAdmin && generationFailure
                ? `${generationFailure.stage}: ${generationFailure.errorMessage} (ref ${generationFailure.generationId})`
                : undefined
            }
            onRetry={
              generationFailure || generationError
                ? () => void generateRecommendationStep(true)
                : undefined
            }
            onManual={
              generationFailure
                ? handleManualFallback
                : generationError
                  ? () => {
                      setRecommendation(null);
                      setGenerationError(null);
                      setStep(3);
                    }
                  : undefined
            }
          />
        )}

        {step === 3 && (
          <form onSubmit={handleSubmit}>
            <div className="grid gap-4">
              {recommendation && excitement && (
                <div className="rounded-lg border border-l-4 border-l-primary bg-slate-50 dark:bg-slate-900/40 px-4 py-3 space-y-2">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant="outline" className={`${scoreColor(usedScore)} gap-1`}>
                        <Zap className="h-3 w-3" />
                        {usedScore.toFixed(1)} {scoreLabel(usedScore)}
                      </Badge>
                      <span className="text-xs text-muted-foreground">
                        Zona:{" "}
                        <strong className="text-foreground">
                          {zoneLabel(recommendation.zone)}
                        </strong>
                      </span>
                      {wasOverridden && (
                        <span className="text-xs text-muted-foreground">
                          (Ajustado de {excitement.excitementScore.toFixed(1)})
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Label htmlFor="allowEdit" className="text-xs cursor-pointer">
                        Editar valores
                      </Label>
                      <Switch
                        id="allowEdit"
                        checked={allowEdit}
                        onCheckedChange={(c) => {
                          const on = Boolean(c);
                          setAllowEdit(on);
                          if (!on) setDeviationReason("");
                        }}
                      />
                    </div>
                  </div>
                  {recommendation.reasoning.justification && (
                    <p className="text-sm whitespace-pre-line">
                      {recommendation.reasoning.justification}
                    </p>
                  )}
                  {recommendation.reasoning.equityCheck?.peerComparison &&
                    !/sem pares/i.test(recommendation.reasoning.equityCheck.peerComparison) && (
                      <p className="text-xs text-muted-foreground">
                        <strong className="text-foreground">Pares:</strong>{" "}
                        {recommendation.reasoning.equityCheck.peerComparison}
                      </p>
                    )}
                </div>
              )}

              <OfferExcitementBlock
                scores={candidateExcitementSeries}
                managerScore={excitement ? usedScore : null}
              />

              {allowEdit && (
                <div className="space-y-2">
                  <Label htmlFor="deviationReason">
                    Motivo da alteração <span className="text-destructive">*</span>
                  </Label>
                  <AutosizeTextarea
                    id="deviationReason"
                    value={deviationReason}
                    onChange={(e) => setDeviationReason(e.target.value)}
                    placeholder="Explique por que está saindo da recomendação..."
                    minHeight={72}
                  />
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="salaryBase">
                    Salário Base Mensal (R$) <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="salaryBase"
                    type="number"
                    step="0.01"
                    value={salaryBase}
                    onChange={(e) => setSalaryBase(e.target.value)}
                    placeholder="10000.00"
                    required
                    disabled={!!recommendation && !allowEdit}
                    className={
                      recommendation && allowEdit && salaryBase !== recommendation.salary
                        ? "border-[hsl(var(--amber-state))]"
                        : undefined
                    }
                  />
                  {bandPosition !== null && matchingBand && (
                    <div className="rounded-md border bg-muted/50 px-3 py-2 text-sm">
                      <span className="text-muted-foreground">Posicionamento: </span>
                      <span
                        className={`font-semibold tabular-nums ${bandPosition < 80 ? "text-[hsl(var(--amber-state))]" : bandPosition <= 100 ? "text-[hsl(var(--forest-pulse))]" : "text-[hsl(var(--comp-coral))]"}`}
                      >
                        {bandPosition.toFixed(1)}%
                      </span>
                      <span className="text-muted-foreground ml-2 text-xs">
                        (banda {matchingBand.key}: R${" "}
                        {(matchingBand.minValue / 100).toLocaleString("pt-BR")} – R${" "}
                        {(matchingBand.maxValue / 100).toLocaleString("pt-BR")})
                      </span>
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="equityShares">Equity (Stock Options)</Label>
                  <Input
                    id="equityShares"
                    type="number"
                    step="1"
                    value={equityShares}
                    onChange={(e) => setEquityShares(e.target.value)}
                    placeholder="1000"
                    disabled={!!recommendation && !allowEdit}
                    className={
                      recommendation && allowEdit && equityShares !== recommendation.equity
                        ? "border-[hsl(var(--amber-state))]"
                        : undefined
                    }
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="signingBonus">Bônus de Assinatura (R$)</Label>
                  <Input
                    id="signingBonus"
                    type="number"
                    step="0.01"
                    value={signingBonus}
                    onChange={(e) => setSigningBonus(e.target.value)}
                    placeholder="0.00"
                    disabled={!!recommendation && !allowEdit}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="performanceBonus">Bônus de Performance (R$)</Label>
                  <Input
                    id="performanceBonus"
                    type="number"
                    step="0.01"
                    value={performanceBonus}
                    onChange={(e) => setPerformanceBonus(e.target.value)}
                    placeholder="0.00"
                    disabled={!!recommendation && !allowEdit}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="performanceBonusShares">Bônus de Performance (Shares)</Label>
                <div className="flex gap-2">
                  <Input
                    id="performanceBonusShares"
                    type="number"
                    step="1"
                    value={performanceBonusShares}
                    onChange={(e) => setPerformanceBonusShares(e.target.value)}
                    placeholder="Mínimo"
                    className="flex-1"
                  />
                  <Input
                    id="performanceBonusSharesMax"
                    type="number"
                    step="1"
                    value={performanceBonusSharesMax}
                    onChange={(e) => setPerformanceBonusSharesMax(e.target.value)}
                    placeholder="Máximo"
                    className="flex-1"
                  />
                </div>
              </div>

              <MarketCompCard
                estimate={marketEstimate}
                offerSalaryMonthly={salaryBaseNum}
                offerEquityAnnual={marketCardEquityAnnualBRL}
                offerSigningBonus={parseFloat(signingBonus) || 0}
                offerPerfBonus={parseFloat(performanceBonus) || 0}
                offerPerfBonusSharesAnnual={marketCardPerfBonusSharesAnnualBRL}
                onEstimateChange={setMarketEstimate}
              />

              <div className="rounded-lg border bg-muted/30 px-4 py-3">
                <div className="flex items-center gap-3">
                  <Checkbox
                    id="allowPaymix"
                    checked={allowPaymixAdjustment}
                    onCheckedChange={(checked) => setAllowPaymixAdjustment(Boolean(checked))}
                    disabled={!canEnablePaymix}
                  />
                  <div>
                    <Label
                      htmlFor="allowPaymix"
                      className={`font-medium ${!canEnablePaymix ? "text-muted-foreground" : ""}`}
                    >
                      Permitir ajuste de PayMix pelo candidato (±{paymixVariationPct}%)
                    </Label>
                    {!canEnablePaymix && (
                      <p className="text-xs text-muted-foreground">
                        Defina equity acima para habilitar esta opção.
                      </p>
                    )}
                  </div>
                </div>
                {allowPaymixAdjustment && (
                  <div className="mt-3 flex items-center gap-2 pl-7">
                    <Label htmlFor="paymixVariationPct" className="text-xs text-muted-foreground">
                      Variação permitida (% do ponto médio)
                    </Label>
                    <Input
                      id="paymixVariationPct"
                      type="number"
                      min={0}
                      max={PAYMIX_MAX_VARIATION_PCT}
                      step={1}
                      value={paymixVariationPct}
                      onChange={(e) => {
                        const raw = Number(e.target.value);
                        const clamped = Number.isFinite(raw)
                          ? Math.min(Math.max(Math.round(raw), 0), PAYMIX_MAX_VARIATION_PCT)
                          : PAYMIX_DEFAULT_VARIATION_PCT;
                        setPaymixVariationPct(clamped);
                      }}
                      className="h-8 w-20"
                    />
                    <span className="text-xs text-muted-foreground">
                      máx. {PAYMIX_MAX_VARIATION_PCT}%
                    </span>
                  </div>
                )}
              </div>

              {salaryBaseNum > 0 && rounds.length > 0 && (
                <div className="rounded-lg border p-4 space-y-2">
                  <h3 className="text-sm font-semibold">PayMix Preview</h3>
                  <PaymixCalculator
                    baseSalary={salaryBaseNum}
                    shares={sharesValue}
                    fxRate={fxRateNum}
                    signingBonus={parseFloat(signingBonus) || 0}
                    performanceBonus={parseFloat(performanceBonus) || 0}
                    performanceBonusShares={parseInt(performanceBonusShares, 10) || 0}
                    performanceBonusSharesMax={parseInt(performanceBonusSharesMax, 10) || undefined}
                    allowSlider={allowPaymixAdjustment}
                    variationPct={paymixVariationPct}
                    rounds={rounds}
                    strikePrice={strikePrice}
                    onValuesChange={handlePaymixValuesChange}
                  />
                </div>
              )}
            </div>

            <DialogFooter className="mt-6">
              {isSimulate ? (
                <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
                  Fechar
                </Button>
              ) : (
                <>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setStep(excitement ? 2 : 1)}
                    disabled={createMutation.isPending}
                  >
                    <ChevronLeft className="mr-1 h-4 w-4" />
                    Voltar
                  </Button>
                  <Button type="submit" disabled={createMutation.isPending}>
                    {createMutation.isPending ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Criando...
                      </>
                    ) : recommendation && allowEdit ? (
                      "Criar com Alterações"
                    ) : (
                      "Criar e Enviar para Aprovação"
                    )}
                  </Button>
                </>
              )}
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
